import { useEffect, useRef, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'
import { Pause, PlayArrow, Stop } from "@mui/icons-material";
import { Box, Button, Grid, Slider } from '@mui/material';

interface WaveformProps {
    audio: string,
    setAudio: (audio: string) => void,
    setAudioBlob: (blob: Blob) => void,
}

const minDistance = 10;

export default function Waveform(props: WaveformProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const waveSurferRef = useRef<WaveSurfer | null>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [startCut, setStartCut] = useState(0.0);
    const [endCut, setEndCut] = useState(1.0);
    const [duration, setDuration] = useState(1.0);
    const [start, setStart] = useState(0.0);
    const [end, setEnd] = useState(100.0);

    const handleSliderChange = (
        event: Event,
        newValue: number | number[],
        activeThumb: number,
    ) => {
        if (!Array.isArray(newValue)) {
            return;
        }

        var localStart = newValue[0];
        var localEnd = newValue[1];

        if (activeThumb === 0) {
            localStart = Math.min(localStart, end - minDistance);
            localEnd = end;
        } else {
            localStart = start;
            localEnd = Math.max(localEnd, start + minDistance)
        }

        setStart(localStart);
        setEnd(localEnd);
        setStartCut(localStart / 100.0 * duration)
        setEndCut(localEnd / 100.0 * duration)

        stop();
    };


    const stop = () => {
        if (waveSurferRef.current) {
            waveSurferRef.current.stop();
            waveSurferRef.current.setTime(startCut);
            setIsPlaying(false);
        }
    }

    const play = () => {
        if (waveSurferRef.current) {
            const current = waveSurferRef.current.getCurrentTime();
            const start1 = Math.max(startCut, current);
            waveSurferRef.current.play(start1);
            setIsPlaying(true);
        }
    }

    const pause = () => {
        if (waveSurferRef.current) {
            waveSurferRef.current.pause();
            setIsPlaying(false);
        }
    }

    function getPeak(array: Float32Array) {
        var max = 0;
        array.forEach(element => {
            if (Math.abs(element) > max) {
                max = Math.abs(element);
            }
        });
        return max;
    }

    useEffect(() => {
        if (containerRef.current) {
            const ws = WaveSurfer.create({
                container: containerRef.current,
                height: 128,
                cursorWidth: 1,
                barWidth: 1,
                barHeight: 5,
                cursorColor: 'purple',
                url: props.audio,
            });
            waveSurferRef.current = ws;

            if (ws) {
                ws.on('ready', () => {
                    var theDuration = ws.getDuration();
                    setEndCut(theDuration);
                    setDuration(theDuration);

                    var buffer = ws.getDecodedData();
                    if (buffer) {
                        var [sliderStart, sliderEnd] = findAutomaticCut(buffer, 0.05, 0.5, 50);
                        setSliderLimits(sliderStart, sliderEnd, theDuration);
                    }
                });

                ws.on('timeupdate', (currentTime) => {
                     if (isPlaying && currentTime >= endCut) {
                         pause();
                         ws.setTime(startCut);
                     }
                });

                ws.on('pause', () => {
                    setIsPlaying(false);
                });
                
                ws.on('play', () => {
                    setIsPlaying(true);
                });

                ws.on('finish', () => { 
                    setIsPlaying(false);
                    stop(); 
                });
            }
        }

        return () => {
            if (waveSurferRef.current) {
                waveSurferRef.current.destroy();
                waveSurferRef.current = null;
            }
        };
        // eslint-disable-next-line
    }, [props.audio])

    async function bufferToBlob(audioBuffer: AudioBuffer): Promise<Blob> {
        return new Promise((resolve) => {
            const numOfChannels = audioBuffer.numberOfChannels;
            const length = audioBuffer.length * numOfChannels * 2 + 44;
            const buffer = new ArrayBuffer(length);
            const view = new DataView(buffer);
            const channels = [];
            let i;
            let sample;
            let offset = 0;
            let pos = 0;

            // write WAVE header
            setUint32(0x46464952);                         // "RIFF"
            setUint32(length - 8);                         // file length - 8
            setUint32(0x45564157);                         // "WAVE"

            setUint32(0x20746d66);                         // "fmt " chunk
            setUint32(16);                                 // length = 16
            setUint16(1);                                  // PCM (uncompressed)
            setUint16(numOfChannels);
            setUint32(audioBuffer.sampleRate);
            setUint32(audioBuffer.sampleRate * 2 * numOfChannels); // avg. bytes/sec
            setUint16(numOfChannels * 2);                  // block-align
            setUint16(16);                                 // 16-bit (hardcoded in this demo)

            setUint32(0x61746164);                         // "data" - chunk
            setUint32(length - pos - 4);                   // chunk length

            // write interleaved data
            for (i = 0; i < numOfChannels; i++)
                channels.push(audioBuffer.getChannelData(i));

            while (pos < length) {
                for (i = 0; i < numOfChannels; i++) {             // interleave channels
                    sample = Math.max(-1, Math.min(1, channels[i][offset])); // clamp
                    sample = (sample < 0 ? sample * 0x8000 : sample * 0x7FFF); // scale to 16-bit signed int
                    view.setInt16(pos, sample, true);          // write 16-bit sample
                    pos += 2;
                }
                offset++;                                     // next sample yuv
            }

            function setUint16(data: any) {
                view.setUint16(pos, data, true);
                pos += 2;
            }

            function setUint32(data: any) {
                view.setUint32(pos, data, true);
                pos += 4;
            }

            resolve(new Blob([buffer], { type: 'audio/wav' }));
        });
    }

    function copyBuffer(fromBuffer: AudioBuffer, fromStart: number, fromEnd: number) {
        var sampleRate = fromBuffer.sampleRate
        var frameCount = Math.max(0, Math.round((fromEnd - fromStart) * sampleRate))
        var newBuffer = new AudioContext().createBuffer(fromBuffer.numberOfChannels, frameCount, sampleRate)

        for (var i = 0; i < fromBuffer.numberOfChannels; i++) {
            var fromChanData = fromBuffer.getChannelData(i)
            var toChanData = new Float32Array(frameCount);
            for (var j = 0, f = Math.round(fromStart * sampleRate); j < frameCount; j++, f++) {
                toChanData[j] = fromChanData[f]
            }
            newBuffer.copyToChannel(toChanData, i);
        }
        return newBuffer;
    }

    async function cutAudio() {
        if (!waveSurferRef.current) return;
        
        var originalBuffer = waveSurferRef.current.getDecodedData();
        if (!originalBuffer) return;

        var newBuffer = copyBuffer(originalBuffer, startCut, endCut);

        setStart(0.0);
        setEnd(100.0);
        setIsPlaying(false);
        setStartCut(0.0);
        setEndCut(1.0);
        waveSurferRef.current.stop();

        bufferToBlob(newBuffer).then((blob: Blob) => {
            props.setAudio(URL.createObjectURL(blob));
            props.setAudioBlob(blob);
        })
    }

    function setSliderLimits(sliderStart: number, sliderEnd: number, durationIn: number) {

        setStart(sliderStart * 100.0);
        setStartCut(sliderStart * durationIn);
        setEnd(sliderEnd * 100.0);
        setEndCut(sliderEnd * durationIn);
        if (waveSurferRef.current) {
            waveSurferRef.current.stop();
            waveSurferRef.current.setTime(sliderStart * durationIn);
            setIsPlaying(false);
        }
    }

    function findAutomaticCut(buffer: AudioBuffer, treshold: number, consecutive: number, resetMax: number) {
        const audioLength = buffer.length;
        var data = buffer.getChannelData(0);
        var peak = getPeak(data);

        var limit = peak * treshold;

        var counter = 0;
        var resetCounter = 0;
        var index = 0;
        var startPos = 0;
        for (let i = 0; i < audioLength; i++) {
            if (Math.abs(data[i]) > limit) {
                if (counter === 0) {
                    index = i;
                }
                resetCounter = 0;
                counter++;
            } else {
                if (resetCounter > resetMax) {
                    counter = 0;
                }
                resetCounter++;
            }
            if (counter > consecutive * audioLength / 100) {
                startPos = index / (audioLength - 1);
                break;
            }
        }

        counter = 0;
        resetCounter = 0;
        index = 0;
        var endPos = 1.0;
        for (let i = audioLength - 1; i >= 0; i--) {
            if (Math.abs(data[i]) > limit) {
                if (counter === 0) {
                    index = i;
                }
                resetCounter = 0;
                counter++;
            } else {
                if (resetCounter > resetMax) {
                    counter = 0;
                }
                resetCounter++;
            }
            if (counter > consecutive * audioLength / 100) {
                endPos = index / (audioLength - 1);
                break;
            }
        }
        return [startPos, endPos];
    }

    return (
        <Box border={2} borderRadius={5} mt={2} alignContent={"center"} justifyContent={"center"}>
            <Box mt={1} mb={1} mr={2} ml={2}>
                <Grid container direction={"row"} wrap={"nowrap"}>
                    {isPlaying ?
                        <Grid>
                            <Button onClick={pause}
                                variant="contained"
                                size="small"
                                sx={{ minWidth: "45px" }}
                            >
                                <Pause />
                            </Button>
                        </Grid>
                        :
                        <Grid>
                            <Button onClick={play}
                                variant="contained"
                                size="small"
                                sx={{ minWidth: "45px" }}
                            >
                                <PlayArrow />
                            </Button>
                        </Grid>
                    }
                    <Grid ml={1}>
                        <Button onClick={stop}
                            variant="contained"
                            size="small"
                            sx={{ minWidth: "45px" }}
                        >
                            <Stop />
                        </Button>
                    </Grid>
                </Grid>
                <div id="waveform" ref={containerRef} />
                <Slider
                    getAriaLabel={() => 'Minimum distance'}
                    value={[start, end]}
                    onChange={handleSliderChange}
                    valueLabelDisplay="auto"
                    disableSwap
                />
            </Box>
            <Box mb={2} ml={1}>
                <Button onClick={cutAudio}
                    variant="contained"
                    size="small"
                    sx={{ minWidth: "45px" }}
                >
                    cut audio
                </Button>
            </Box>
        </Box>
    )
}
